import { scannerJobs } from '../../lib/scannerJobs.js';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Camera, ChevronDown, ChevronUp, FileText, FileUp, Link2, RotateCw, Scissors, Send, TriangleAlert, X } from 'lucide-react';
import type {
  InvoiceDirection,
  InvoiceFolder,
  InvoiceScanReadResult,
  InvoiceScanSaveResult,
} from '@worki/shared';
import { CURRENCIES, currencyStep, INVOICE_DIRECTIONS, invoiceArchiveStart } from '@worki/shared';
import { currentLocale } from '../../i18n.js';
import { api, RequestError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatDate, money } from '../../lib/format.js';
import { blurScore, isBlurry, regionOf } from '../../lib/scan/blur.js';
import { INVOICE_DRAFT_KEY } from '../../lib/scan/draft.js';
import { decodePhoto, findPage } from '../../lib/scan/pipeline.js';
import { InfoTip } from '../InfoTip.js';
import { ScannerButton } from '../ScannerButton.js';
import { Modal } from '../Modal.js';
import { QuickScanDialog, shrinkPicked, totalsByCurrency } from '../QuickScan.js';
import { useToast } from '../Toast.js';
import { Badge, Button, ErrorNotice, Field, IconButton, Input, Select, Spinner } from '../ui.js';
import { pdfPages, pdfThumb, type PdfPages } from './pdfPages.js';
import { rotatePickedInvoicePage } from './rotatePickedPage.js';
import {
  beforeArchive,
  blockedBy,
  canJoinPrevious,
  dayString,
  duplicateOf,
  edit,
  errorsOnto,
  isDuplicate,
  joinPrevious,
  manifestOf,
  missingOf,
  partsOf,
  rowsFromRead,
  removeScanRow,
  setDirection,
  split,
  type EditableField,
  type FolderChoice,
  type RequiredField,
  type ScanEntry,
  type ScanRow,
} from './rows.js';

/**
 * Section 3 (Nitzan, 05.10) — the invoices' quick scan: "בחרו חשבונית אחת או כמה".
 *
 * It took the place of "הוספת חשבונית" (section 6): an invoice comes in through
 * the camera or as a file, never typed in without one. Every file is an invoice
 * of its own; a PDF can contain several independently reviewed page selections. Photographs of the same invoice
 * are joined by the server once it has read them, and can be split again here.
 *
 * Three screens in one dialog. The opening one is the two ways in (only the file
 * on a desktop, which has no camera worth holding up to paper). Then the files,
 * while they are made ready and read, which is where they stay if the read
 * fails. Then the summary: a row an invoice, each its own expense or income, and
 * nothing filed until every row has its amount, its party, its date and its
 * number, is not one the archive would hide, and is not a copy of one already in
 * the list. The folder is optional, and "שמירה
 * ושליחה" goes on to the folder share.
 *
 * Closing with the ✕ keeps it all for as long as the page is open; "ביטול" is
 * what throws it away. No drag-and-drop: files are chosen.
 */

/** What the file picker offers: what the server reads as an invoice. */
const ACCEPT = 'application/pdf,.pdf,image/jpeg,image/png';

const isPdf = (file: File): boolean => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
const isImage = (file: File): boolean => file.type === 'image/jpeg' || file.type === 'image/png';

let entrySeq = 0;

/** Section 3 — a picked photograph's blur, measured on the page where one is found. */
async function blurryPhoto(file: File): Promise<boolean> {
  try {
    const decoded = await decodePhoto(file);
    const width = decoded instanceof ImageBitmap ? decoded.width : decoded.naturalWidth;
    const height = decoded instanceof ImageBitmap ? decoded.height : decoded.naturalHeight;
    const score = await blurScore(decoded, regionOf(findPage(decoded), width, height));
    if (decoded instanceof ImageBitmap) decoded.close();
    return score !== null && isBlurry(score);
  } catch {
    return false;
  }
}

const NEW_FOLDER = '__new__';

export function InvoiceScanDialog({
  open,
  onClose,
  camera,
  onSaved,
  initialFolderId = '',
  initialFiles,
  backgroundReview = false,
  onDiscard,
}: {
  open: boolean;
  onClose: () => void;
  /** A phone, which has the camera. A desktop is offered the file alone. */
  camera: boolean;
  onSaved: (result: InvoiceScanSaveResult, then: 'list' | 'share') => void;
  initialFolderId?: string;
  initialFiles?: File[];
  backgroundReview?: boolean;
  onDiscard?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const locale = currentLocale();
  const pickRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<'start' | 'files' | 'summary'>('start');
  const [entries, setEntries] = useState<ScanEntry[]>([]);
  const entriesRef = useRef<ScanEntry[]>([]);
  entriesRef.current = entries;
  /** Files being made ready (shrunk, measured for blur), before they are read. */
  const [preparing, setPreparing] = useState<{ done: number; total: number } | null>(null);
  const [refusedPick, setRefusedPick] = useState(false);
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [shooting, setShooting] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [folderPick, setFolderPick] = useState(initialFolderId);
  useEffect(() => { setFolderPick(initialFolderId); }, [initialFolderId]);
  const [folderName, setFolderName] = useState('');
  /** Save was pressed: from here every field that lacks something says so. */
  const [tried, setTried] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lastThen = useRef<'list' | 'share'>('list');
  const rowsOfFile = useRef<number[][]>([]);

  const archiveStart = useMemo(() => invoiceArchiveStart(), [open]);
  const archiveDate = formatDate(dayString(archiveStart), locale);

  const folders = useQuery({
    queryKey: ['invoice-folders'],
    queryFn: () => api.get<{ items: InvoiceFolder[] }>('/invoices/folders'),
    enabled: open && stage === 'summary',
  });

  const reset = () => {
    for (const entry of entriesRef.current) if (entry.url) URL.revokeObjectURL(entry.url);
    setEntries([]);
    setRows([]);
    setStage('start');
    setPreparing(null);
    setRefusedPick(false);
    setViewing(null);
    setFolderPick(initialFolderId);
    setFolderName('');
    setTried(false);
    setSaveError(null);
    read.reset();
    save.reset();
  };

  /** "ביטול": the scan is thrown away. The ✕ keeps it. */
  const discard = () => {
    if (scannerJobs.getSnapshot()?.origin.context === `invoices:${initialFolderId}`) void scannerJobs.cancel();
    reset();
    onDiscard?.();
    onClose();
  };

  // Let go of every picture when the page that holds this dialog goes.
  useEffect(
    () => () => {
      for (const entry of entriesRef.current) if (entry.url) URL.revokeObjectURL(entry.url);
    },
    [],
  );

  /* ---- reading ------------------------------------------------------------ */

  const read = useMutation({
    mutationFn: async (list: ScanEntry[]) => {
      const form = new FormData();
      for (const entry of list) form.append('files', entry.file, entry.file.name);
      return { list, result: await api.upload<InvoiceScanReadResult>('/invoices/scan/read', form) };
    },
    onSuccess: ({ list, result }) => {
      const readEntries = new Map(list.map((entry, index) => [entry.id, { ...entry, reading: result.files[index] ?? null, refused: null }]));
      setEntries((current) => current.map((entry) => readEntries.get(entry.id) ?? entry));
      setRows((current) => [...current, ...rowsFromRead(result, list.map((entry) => entry.id))]);
      setTried(false);
      setSaveError(null);
      setStage('summary');
    },
    onError: (cause, list) => {
      const fields = cause instanceof RequestError ? cause.fields : {};
      const failures = new Map(list.map((entry, index) => [entry.id, fields[`files.${index}`] ?? null]));
      setEntries((current) => current.map((entry) => failures.has(entry.id) ? { ...entry, refused: failures.get(entry.id) ?? null } : entry));
    },
  });

  /** Files picked or photographed: made ready one at a time, then all of them read together. */
  const take = async (picked: File[]) => {
    const usable = picked.filter((file) => isPdf(file) || isImage(file));
    setRefusedPick(usable.length < picked.length);
    if (usable.length === 0) return;
    setStage('files');
    read.reset();
    const before = entriesRef.current;
    const added: ScanEntry[] = [];
    for (const [index, chosen] of usable.entries()) {
      setPreparing({ done: index, total: usable.length });
      const pdf = isPdf(chosen);
      // 1.46 — a gallery photograph is sent the size the camera's own are.
      const file = pdf ? chosen : await shrinkPicked(chosen);
      added.push({
        id: `scan-file-${(entrySeq += 1)}`,
        file,
        url: pdf ? null : URL.createObjectURL(file),
        pdf,
        blurry: pdf ? false : await blurryPhoto(file),
        reading: null,
        refused: null,
      });
      // Listed as each is ready, so the wait has something to show for itself.
      setEntries([...before, ...added]);
    }
    setPreparing(null);
    read.mutate([...before.filter((entry) => !entry.reading), ...added]);
  };

  const acceptedInitial = useRef<File[] | undefined>(undefined);
  useEffect(() => {
    if (open && initialFiles?.length && acceptedInitial.current !== initialFiles) {
      acceptedInitial.current = initialFiles;
      void take(initialFiles);
    }
  }, [open, initialFiles]);

  const removeEntry = (id: string) => {
    const gone = entries.find((entry) => entry.id === id);
    if (gone?.url) URL.revokeObjectURL(gone.url);
    const left = entries.filter((entry) => entry.id !== id);
    setEntries(left);
    if (left.length === 0) reset();
    else if (left.every((entry) => entry.reading)) setStage('summary');
  };

  const rotateEntryPage = async (id: string, pageNumber: number) => {
    const entry = entriesRef.current.find(item => item.id === id);
    if (!entry) return;
    const file = await rotatePickedInvoicePage(entry.file, pageNumber);
    const url = entry.pdf ? null : URL.createObjectURL(file);
    if (!entriesRef.current.some(item => item.id === id && item.file === entry.file)) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    if (entry.url) URL.revokeObjectURL(entry.url);
    setEntries(current => current.map(item => item.id === id ? { ...item, file, url } : item));
  };

  /* ---- the summary ------------------------------------------------------- */

  const change = (id: string, update: (row: ScanRow) => ScanRow) => {
    setSaveError(null);
    setRows((current) => current.map((row) => (row.id === id ? update(row) : row)));
  };

  const removeRow = (id: string) => {
    if (save.isPending) return;
    const remaining = removeScanRow(rows, entries, id);
    if (remaining.rows.length === 0) {
      reset();
      return;
    }
    for (const entry of remaining.removedEntries) if (entry.url) URL.revokeObjectURL(entry.url);
    setEntries(remaining.entries);
    setRows(remaining.rows);
    if (viewing === id) setViewing(null);
    setSaveError(null);
  };

  const folderChoice: FolderChoice =
    folderPick === NEW_FOLDER
      ? { kind: 'new', name: folderName }
      : folderPick
        ? { kind: 'existing', id: folderPick }
        : { kind: 'none' };
  const folderMissing = folderChoice.kind === 'new' && folderChoice.name.trim() === '';

  const save = useMutation({
    mutationFn: async (then: 'list' | 'share') => {
      const built = manifestOf(rows, entries, folderChoice);
      rowsOfFile.current = built.rowsOfFile;
      const form = new FormData();
      for (const file of built.files) form.append('files', file, file.name);
      form.append('manifest', JSON.stringify(built.manifest));
      return { then, result: await api.upload<InvoiceScanSaveResult>('/invoices/scan/save', form) };
    },
    onSuccess: ({ then, result }) => {
      toast.success(t('invoiceScan.saved', { count: result.ids.length }));
      reset();
      onSaved(result, then);
      onClose();
    },
    onError: (cause) => {
      const failure = cause instanceof RequestError ? cause : null;
      const fields = failure?.fields ?? {};
      if (failure && (failure.status === 400 || failure.status === 409) && Object.keys(fields).length > 0) {
        // Put back on the rows it was about, which open to show it.
        const placed = errorsOnto(rows, fields, rowsOfFile.current);
        setRows(placed.rows);
        setTried(true);
        setSaveError(placed.general[0] ?? null);
        return;
      }
      setSaveError(failure?.code ?? 'errors.unknown');
    },
  });

  const blocked = rows.filter((row) => blockedBy(row, archiveStart).length > 0);

  const submit = (then: 'list' | 'share') => {
    lastThen.current = then;
    setTried(true);
    setSaveError(null);
    if (blocked.length > 0 || folderMissing) {
      setRows((current) => current.map((row) => (blockedBy(row, archiveStart).length > 0 ? { ...row, open: true } : row)));
      // The first row that needs the owner, brought into view.
      const first = blocked[0]?.id ?? (folderMissing ? 'invoice-scan-folder' : null);
      if (first) requestAnimationFrame(() => document.getElementById(first)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      return;
    }
    save.mutate(then);
  };

  const viewed = rows.find((row) => row.id === viewing) ?? null;

  /* ---- the screens --------------------------------------------------------- */

  const busy = preparing !== null || read.isPending;
  const readFailed = stage === 'files' && read.isError && !busy;
  const readFields = read.error instanceof RequestError ? read.error.fields : {};
  const perFile = Object.keys(readFields).some((key) => /^files\.\d+$/.test(key));
  const readError = readFailed
    ? (readFields.files ?? (perFile ? null : read.error instanceof RequestError ? read.error.code : 'errors.unknown'))
    : null;

  const footer =
    stage === 'start' ? null : stage === 'files' ? (
      <>
        <Button type="button" variant="ghost" onClick={discard}>
          {t('common.cancel')}
        </Button>
        {readFailed && (
          <Button
            type="button"
            variant="secondary"
            icon={<FileUp className="h-4 w-4" aria-hidden />}
            onClick={() => pickRef.current?.click()}
          >
            {t('invoiceScan.addFiles')}
          </Button>
        )}
        {readFailed && (
          <Button type="button" variant="primary" onClick={() => read.mutate(entries.filter((entry) => !entry.reading))}>
            {t('common.retry')}
          </Button>
        )}
      </>
    ) : (
      <div className="flex w-full flex-wrap items-center justify-end gap-2">
        {save.isPending && (
          <p role="status" className="flex min-w-0 basis-full items-center gap-2 text-xs text-muted-foreground">
            <Spinner />
            {t('invoiceScan.saving', { count: rows.length })}
          </p>
        )}
        {!save.isPending && saveError && (
          <div className="flex basis-full flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1">
              <ErrorNotice code={saveError} />
            </div>
            <Button type="button" size="sm" variant="secondary" onClick={() => submit(lastThen.current)}>
              {t('common.retry')}
            </Button>
          </div>
        )}
        {!save.isPending && !saveError && tried && blocked.length > 0 && (
          <p role="alert" className="basis-full text-xs font-medium text-warning">
            {t('invoiceScan.blocked', { count: blocked.length })}
          </p>
        )}
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={discard}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={save.isPending}
          icon={<FileUp className="h-4 w-4" aria-hidden />}
          onClick={() => pickRef.current?.click()}
        >
          {t('invoiceScan.addFiles')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={save.isPending}
          loading={save.isPending && save.variables === 'list'}
          onClick={() => submit('list')}
        >
          {t('common.save')}
        </Button>
        {!backgroundReview && <Button
          type="button"
          variant="primary"
          disabled={save.isPending}
          loading={save.isPending && save.variables === 'share'}
          icon={<Send className="h-4 w-4" aria-hidden />}
          onClick={() => submit('share')}
        >
          {t('invoiceScan.saveSend')}
        </Button>}
      </div>
    );

  return (
    <>
      <Modal
        open={open && !shooting}
        title={t('scan.title')}
        onClose={onClose}
        size={stage === 'summary' ? 'wide' : 'default'}
        footer={footer}
      >
        <input
          ref={pickRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            void take(files);
          }}
        />

        {!backgroundReview && <ScannerButton contextKey={`invoices:${initialFolderId}`} active={open && !shooting} disabled={busy || save.isPending} onScanned={file => take([file])} />}
        {refusedPick && <ErrorNotice code="validation.invoice.fileType" />}

        {stage === 'start' && (
          <div className="space-y-4 py-2">
            <div className="space-y-1 text-center">
              <p className="text-base font-semibold">{t('invoiceScan.choose')}</p>
              <p className="text-sm text-muted-foreground">{t('invoiceScan.chooseHint')}</p>
            </div>
            <div className={cn('grid gap-3', camera ? 'grid-cols-2' : 'mx-auto max-w-xs grid-cols-1')}>
              {camera && (
                <WayIn
                  icon={<Camera className="h-7 w-7" aria-hidden />}
                  label={t('invoiceScan.camera')}
                  hint={t('invoiceScan.cameraHint')}
                  onClick={() => setShooting(true)}
                />
              )}
              <WayIn
                icon={<FileUp className="h-7 w-7" aria-hidden />}
                label={t('invoiceScan.file')}
                hint={t('invoiceScan.fileHint')}
                onClick={() => pickRef.current?.click()}
              />
            </div>
          </div>
        )}

        {stage === 'files' && (
          <div className="space-y-3">
            {preparing && (
              <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner />
                {t('invoiceScan.preparing', { current: preparing.done + 1, total: preparing.total })}
              </p>
            )}
            {!preparing && read.isPending && (
              <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner />
                {t('invoiceScan.reading', { count: entries.length })}
              </p>
            )}
            {readError && <ErrorNotice code={readError} />}
            {readFailed && perFile && <ErrorNotice code="invoiceScan.fileRefused" />}
            <FileList entries={entries.filter((entry) => !entry.reading)} busy={busy} onRemove={removeEntry} />
          </div>
        )}

        {stage === 'summary' && (
          <div className="space-y-3">
            <Totals rows={rows} />
            {rows.some((row) => row.pdfPages) && <p className="text-xs text-muted-foreground">{t('invoiceScan.pdfSplitNotice')}</p>}
            <ol className="space-y-2.5">
              {rows.map((row, index) => (
                <RowCard
                  key={row.id}
                  row={row}
                  index={index}
                  entries={entries}
                  tried={tried}
                  archiveStart={archiveStart}
                  archiveDate={archiveDate}
                  disabled={save.isPending}
                  canJoin={canJoinPrevious(rows, index)}
                  onChange={(update) => change(row.id, update)}
                  onRemove={() => removeRow(row.id)}
                  onView={() => setViewing(row.id)}
                  onSplit={() =>
                    setRows((current) => current.flatMap((entry) => (entry.id === row.id ? split(entry, entries) : [entry])))
                  }
                  onJoin={() => setRows((current) => joinPrevious(current, index))}
                />
              ))}
            </ol>

            <div id="invoice-scan-folder" className="space-y-2 rounded-xl border border-border p-3">
              <Field label={t('invoiceScan.folder')} htmlFor="invoice-scan-folder-pick" hint={t('invoiceScan.folderHint')}>
                <Select
                  id="invoice-scan-folder-pick"
                  value={folderPick}
                  disabled={save.isPending}
                  onChange={(value) => {
                    setFolderPick(value);
                    setSaveError(null);
                  }}
                >
                  <option value="">{t('invoiceScan.folderNone')}</option>
                  {(folders.data?.items ?? []).map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                  <option value={NEW_FOLDER}>{t('invoiceScan.folderNew')}</option>
                </Select>
              </Field>
              {folderPick === NEW_FOLDER && (
                <Field
                  label={t('invoiceScan.folderName')}
                  htmlFor="invoice-scan-folder-name"
                  {...(tried && folderMissing ? { error: t('validation.folderName.required') } : {})}
                >
                  <Input
                    id="invoice-scan-folder-name"
                    value={folderName}
                    maxLength={60}
                    disabled={save.isPending}
                    invalid={tried && folderMissing}
                    onChange={(event) => setFolderName(event.target.value)}
                  />
                </Field>
              )}
            </div>
          </div>
        )}
      </Modal>

      {camera && (
        <QuickScanDialog
          open={open && shooting}
          onClose={() => setShooting(false)}
          title={t('scan.title')}
          camera
          checkBlur
          draftKey={INVOICE_DRAFT_KEY}
          onPages={(pages) => void take(pages)}
        />
      )}

      <PagesViewer row={viewed} entries={entries} onClose={() => setViewing(null)} onRotate={rotateEntryPage} />
    </>
  );
}

/** One of the two ways in, as a big button. */
function WayIn({ icon, label, hint, onClick }: { icon: ReactNode; label: string; hint: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-muted/40 p-4 text-center transition-colors hover:bg-muted active:scale-[0.98]"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</span>
      <span className="text-base font-semibold">{label}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  );
}

/** A file's picture: the photograph, or a PDF's first page once it is drawn. */
function Thumb({ entry, pageNumbers }: { entry: ScanEntry | undefined; pageNumbers?: readonly number[] | undefined }) {
  const [page, setPage] = useState<string | null>(null);
  useEffect(() => {
    if (!entry?.pdf) return undefined;
    setPage(null);
    let alive = true;
    void pdfThumb(entry.file, pageNumbers).then((url) => {
      if (alive) setPage(url);
    });
    return () => {
      alive = false;
    };
  }, [entry, pageNumbers]);
  const src = entry?.url ?? page;
  return src ? (
    <img src={src} alt="" className="h-full w-full object-cover object-top" />
  ) : (
    <FileText className="h-6 w-6 text-muted-foreground" aria-hidden />
  );
}

/** The files while they are made ready and read, and where they stay if the read fails. */
function FileList({
  entries,
  busy,
  onRemove,
}: {
  entries: ScanEntry[];
  busy: boolean;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  return (
    <ul className="divide-y divide-border rounded-xl border border-border">
      {entries.map((entry) => (
        <li key={entry.id} className="flex items-center gap-3 p-2">
          <span className="flex h-14 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
            <Thumb entry={entry} />
          </span>
          <span className="min-w-0 flex-1 space-y-0.5">
            <span className="block truncate text-sm" dir="auto">
              {entry.file.name}
            </span>
            {entry.blurry && <BlurMark />}
            {entry.refused && (
              <span className="block text-xs text-destructive">
                {t(entry.refused, { defaultValue: t('invoiceScan.fileRefused') })}
              </span>
            )}
          </span>
          {!busy && (
            <IconButton label={t('invoiceScan.removeFile', { name: entry.file.name })} onClick={() => onRemove(entry.id)}>
              <X className="h-4 w-4" aria-hidden />
            </IconButton>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Section 3 — soft, and never in the way: a blurry photograph is still filed if the owner wants it. */
function BlurMark() {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1 text-xs text-warning">
      <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {t('invoiceScan.blur.mark')}
    </span>
  );
}

/** How many invoices, and what they come to a currency at a time; expenses and income apart when both are there. */
export function Totals({ rows }: { rows: ScanRow[] }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const expense = rows.filter((row) => row.direction === 'expense');
  const income = rows.filter((row) => row.direction === 'income');
  const lines =
    expense.length > 0 && income.length > 0
      ? [
          { key: 'expense', label: t('invoiceScan.totalExpense'), rows: expense },
          { key: 'income', label: t('invoiceScan.totalIncome'), rows: income },
        ]
      : [{ key: 'all', label: t('invoiceScan.total'), rows }];
  return (
    <div className="space-y-1.5 rounded-xl border border-primary/20 bg-primary/5 p-3">
      <p className="text-sm font-semibold text-primary" role="status">
        {t('invoiceScan.count', { count: rows.length })}
      </p>
      {lines.map((line) => {
        const totals = totalsByCurrency(partsOf(line.rows));
        return (
          <div key={line.key} className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className={cn('text-xs', line.key === 'income' ? 'text-success' : 'text-muted-foreground')}>
              {line.label}
            </span>
            <span className={cn('flex flex-wrap gap-x-3 text-base font-semibold', line.key === 'income' && 'text-success')}>
              {totals.length === 0
                ? '—'
                : totals.map(({ currency, total }) => (
                    <span key={currency} className="ltr-nums">
                      {money(total, currency, locale)}
                    </span>
                  ))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const LABEL: Record<RequiredField, string> = {
  amount: 'invoiceScan.amount',
  party: 'invoiceScan.supplier',
  issuedAt: 'invoiceScan.date',
  number: 'invoiceScan.number',
};

const VALIDATION: Record<RequiredField, string> = {
  amount: 'validation.invoice.amount',
  party: 'validation.invoice.party',
  issuedAt: 'validation.invoice.date',
  number: 'validation.invoice.number',
};

/** One invoice of the summary: compact, and opened to edit. Exported for its test. */
export function RowCard({
  row,
  index,
  entries,
  tried,
  archiveStart,
  archiveDate,
  disabled,
  onChange,
  onRemove,
  onView,
  onSplit,
  onJoin,
  canJoin,
}: {
  row: ScanRow;
  index: number;
  entries: ScanEntry[];
  tried: boolean;
  archiveStart: Date;
  archiveDate: string;
  disabled: boolean;
  onChange: (update: (row: ScanRow) => ScanRow) => void;
  onRemove: () => void;
  onView: () => void;
  onSplit: () => void;
  onJoin: () => void;
  canJoin?: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const own = row.files.map((id) => entries.find((entry) => entry.id === id)).filter(Boolean) as ScanEntry[];
  const missing = missingOf(row);
  const old = beforeArchive(row, archiveStart);
  const duplicate = isDuplicate(row);
  const reasons = blockedBy(row, archiveStart);
  const partyLabel = t(row.direction === 'income' ? 'invoiceScan.customer' : 'invoiceScan.supplier');
  const title =
    [row.party.trim(), row.number.trim()].filter(Boolean).join(' · ') || t('invoiceScan.untitled', { index: index + 1 });
  const amount = Number(row.amount);
  const pages = row.pdfPages?.length ?? Math.max(row.read.pages, row.files.length);
  const mayJoin = (canJoin ?? index > 0) && !row.pdfPages;
  const maySplit = !row.pdfPages && row.joined && row.files.length > 1;
  const field = (name: RequiredField) => `${row.id}-${name}`;
  const errorOf = (name: RequiredField): string | undefined => {
    const server = row.errors[name];
    if (server) return t(server, { date: archiveDate });
    if (tried && missing.includes(name)) return t(VALIDATION[name]);
    return undefined;
  };
  const set = (name: EditableField) => (value: string) => onChange((current) => edit(current, name, value));

  return (
    <li
      id={row.id}
      className={cn(
        'overflow-hidden rounded-xl border bg-card',
        reasons.length === 0
          ? 'border-border'
          : old || row.errors.files || (tried && reasons.length > 0)
            ? 'border-destructive/50'
            : 'border-warning/50',
      )}
    >
      <div className="flex gap-3 p-3">
        <button
          type="button"
          onClick={onView}
          aria-label={t('invoiceScan.preview', { name: title })}
          className="relative flex h-[4.5rem] w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted sm:h-20 sm:w-16"
        >
          <Thumb entry={own[0]} pageNumbers={row.pdfPages} />
          {pages > 1 && (
            <span className="absolute bottom-0.5 end-0.5 rounded bg-background/90 px-1 text-[10px] font-semibold ltr-nums">
              {pages}
            </span>
          )}
        </button>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start gap-1">
            <button
              type="button"
              onClick={() => onChange((current) => ({ ...current, open: !current.open }))}
              aria-expanded={row.open}
              aria-label={t('invoiceScan.edit', { name: title })}
              className="flex min-w-0 flex-1 items-center gap-1 text-start"
            >
              <span className="min-w-0 truncate text-sm font-semibold" dir="auto">
                {title}
              </span>
              {row.open ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              )}
            </button>
            <IconButton
              label={t('invoiceScan.remove', { name: title })}
              className="-me-1.5 -mt-1.5"
              disabled={disabled}
              onClick={onRemove}
            >
              <X className="h-4 w-4" aria-hidden />
            </IconButton>
          </div>

          <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            <span className={cn('ltr-nums', row.direction === 'income' && 'text-success')}>
              {missing.includes('amount') ? '—' : money(amount, row.currency, locale)}
            </span>
            <span>·</span>
            <span className="ltr-nums">{missing.includes('issuedAt') ? '—' : formatDate(row.issuedAt, locale)}</span>
          </p>
          {row.pdfPages && <p className="text-xs text-muted-foreground">{t('invoiceScan.pdfPageRange', { from: row.pdfPages[0], to: row.pdfPages.at(-1), count: row.pdfPages.length })}</p>}

          <div className="flex flex-wrap items-center gap-1.5">
            <div
              role="group"
              aria-label={t('invoiceScan.direction')}
              className="inline-flex rounded-lg bg-muted p-0.5 text-xs font-medium"
            >
              {INVOICE_DIRECTIONS.map((direction: InvoiceDirection) => (
                <button
                  key={direction}
                  type="button"
                  aria-pressed={row.direction === direction}
                  disabled={disabled}
                  onClick={() => onChange((current) => setDirection(current, direction))}
                  className={cn(
                    'min-h-7 rounded-md px-2.5 transition-colors',
                    row.direction === direction
                      ? direction === 'income'
                        ? 'bg-success text-white shadow-sm'
                        : 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`invoiceScan.${direction}`)}
                </button>
              ))}
            </div>
            <InfoTip label={t('common.explain')}>{t('invoiceScan.directionInfo')}</InfoTip>
            {own.some((entry) => entry.blurry) && <BlurMark />}
          </div>
        </div>
      </div>

      {/* What stands between the row and the save, said on the row whether it is open or not. */}
      {(missing.length > 0 || old || duplicate || row.errors.files) && (
        <div className="space-y-1.5 px-3 pb-3">
          {missing.length > 0 && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
              <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {t('invoiceScan.missing', {
                fields: missing.map((name) => (name === 'party' ? partyLabel : t(LABEL[name]))).join(', '),
              })}
            </p>
          )}
          {old && (
            <p role="alert" className="rounded-lg bg-destructive/10 px-2.5 py-1.5 text-xs font-medium text-destructive">
              {t('validation.invoice.beforeArchive', { date: archiveDate })}
            </p>
          )}
          {row.errors.files && (
            <p role="alert" className="rounded-lg bg-destructive/10 px-2.5 py-1.5 text-xs font-medium text-destructive">
              {t(row.errors.files, { defaultValue: t('invoiceScan.fileRefused') })}
            </p>
          )}
          {duplicate && (
            <p role="alert" className="rounded-lg bg-warning/10 px-2.5 py-1.5 text-xs text-warning">
              {t(duplicateOf(row) ? 'invoiceScan.duplicateBlocked' : 'invoiceScan.duplicateChanged')}
              {duplicateOf(row) && (
                <span className="block" dir="auto">
                  {t('invoiceScan.duplicateLocation', {
                    defaultValue: '{{number}} · {{folder}}',
                    number: duplicateOf(row)?.number ?? row.number,
                    folder: duplicateOf(row)?.folderName ?? t('invoiceScan.folderNone'),
                  })}
                </span>
              )}
              {duplicateOf(row)?.issuedAt && <span className="ms-2 ltr-nums">{formatDate(row.issuedAt, locale)}</span>}
            </p>
          )}
        </div>
      )}

      {row.open && (
        <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2">
          <Field label={partyLabel} htmlFor={field('party')} {...(errorOf('party') ? { error: errorOf('party') } : {})}>
            <Input
              id={field('party')}
              value={row.party}
              maxLength={120}
              dir="auto"
              disabled={disabled}
              invalid={Boolean(errorOf('party'))}
              onChange={(event) => set('party')(event.target.value)}
            />
          </Field>
          <Field
            label={t('invoiceScan.number')}
            htmlFor={field('number')}
            {...(errorOf('number') ? { error: errorOf('number') } : {})}
          >
            <Input
              id={field('number')}
              value={row.number}
              maxLength={60}
              dir="ltr"
              disabled={disabled}
              invalid={Boolean(errorOf('number'))}
              onChange={(event) => set('number')(event.target.value)}
            />
          </Field>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Field
                label={t('invoiceScan.amount')}
                htmlFor={field('amount')}
                {...(errorOf('amount') ? { error: errorOf('amount') } : {})}
              >
                <Input
                  id={field('amount')}
                  type="number"
                  inputMode="decimal"
                  step={currencyStep(row.currency)}
                  min="0"
                  dir="ltr"
                  value={row.amount}
                  disabled={disabled}
                  invalid={Boolean(errorOf('amount'))}
                  onChange={(event) => set('amount')(event.target.value)}
                />
              </Field>
            </div>
            <div className="w-32 shrink-0">
              <Field label={t('invoiceScan.currency')} htmlFor={`${row.id}-currency`}>
                <Select id={`${row.id}-currency`} value={row.currency} disabled={disabled} onChange={set('currency')}>
                  {CURRENCIES.map((option) => (
                    <option key={option} value={option}>
                      {t('currencies.description', { code: option, name: t(`currencies.${option}`) })}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
          <Field
            label={t('invoiceScan.date')}
            htmlFor={field('issuedAt')}
            {...(errorOf('issuedAt') ? { error: errorOf('issuedAt') } : {})}
          >
            <Input
              id={field('issuedAt')}
              type="date"
              dir="ltr"
              value={row.issuedAt}
              disabled={disabled}
              invalid={Boolean(errorOf('issuedAt')) || old}
              onChange={(event) => set('issuedAt')(event.target.value)}
            />
          </Field>
        </div>
      )}

      {(maySplit || mayJoin) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
          {maySplit && (
            <>
              <Badge tone="info">{t('invoiceScan.joined', { count: pages })}</Badge>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled}
                icon={<Scissors className="h-3.5 w-3.5" aria-hidden />}
                onClick={onSplit}
              >
                {t('invoiceScan.split')}
              </Button>
            </>
          )}
          {mayJoin && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ms-auto"
              disabled={disabled}
              icon={<Link2 className="h-3.5 w-3.5" aria-hidden />}
              onClick={onJoin}
            >
              {t('invoiceScan.joinPrevious')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/** An invoice enlarged: every page of every file it holds. */
function PagesViewer({ row, entries, onClose, onRotate }: { row: ScanRow | null; entries: ScanEntry[]; onClose: () => void; onRotate: (id: string, page: number) => Promise<void> }) {
  const { t } = useTranslation();
  const own = (row?.files ?? []).map((id) => entries.find((entry) => entry.id === id)).filter(Boolean) as ScanEntry[];
  const [drawn, setDrawn] = useState<Record<string, PdfPages | null>>({});
  const [rotating, setRotating] = useState(false);
  const [rotateFailed, setRotateFailed] = useState(false);
  const rotate = async (id: string, page: number) => {
    setRotating(true);
    setRotateFailed(false);
    try { await onRotate(id, page); }
    catch { setRotateFailed(true); }
    finally { setRotating(false); }
  };
  const rotationButton = (id: string, page: number) => (
    <Button type="button" size="sm" variant="secondary" disabled={rotating} onClick={() => void rotate(id, page)} aria-label={t('scan.rotateRight')}>
      <RotateCw className="h-4 w-4" aria-hidden />{t('scan.rotateRight')}
    </Button>
  );

  useEffect(() => {
    let alive = true;
    setDrawn({});
    for (const entry of own) {
      if (!entry.pdf) continue;
      const key = `${entry.id}:${row?.pdfPages?.join(',') ?? '*'}`;
      void pdfPages(entry.file, row?.pdfPages).then((pages) => {
        if (alive) setDrawn((current) => ({ ...current, [key]: pages }));
      });
    }
    return () => {
      alive = false;
    };
    // Cache belongs to the file plus its selected original pages, including when reopened.
  }, [row, entries]);

  const title = row ? [row.party.trim(), row.number.trim()].filter(Boolean).join(' · ') || t('scan.title') : '';
  let pageNumber = 0;

  return (
    <Modal open={row !== null} title={title} onClose={() => { if (!rotating) onClose(); }} size="full">
      <div className="space-y-3">
        {rotateFailed && <ErrorNotice code="errors.unknown" />}
        {own.map((entry) => {
          if (!entry.pdf) {
            pageNumber += 1;
            return (
              <div key={entry.id} className="space-y-2">
                {rotationButton(entry.id, 1)}
                <img
                src={entry.url ?? ''}
                alt={t('invoiceScan.page', { index: pageNumber })}
                className="mx-auto w-full max-w-2xl rounded-lg border border-border"
                />
              </div>
            );
          }
          const pages = drawn[`${entry.id}:${row?.pdfPages?.join(',') ?? '*'}`];
          if (!pages) {
            return (
              <p key={entry.id} role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner />
                <span dir="auto">{entry.file.name}</span>
              </p>
            );
          }
          return (
            <div key={entry.id} className="space-y-3">
              {pages.pages.length === 0 && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-5 w-5" aria-hidden />
                  <span dir="auto">{entry.file.name}</span>
                </p>
              )}
              {pages.pages.map((src, index) => {
                pageNumber += 1;
                return (
                  <div key={`${entry.id}:${pages.pageNumbers[index]}`} className="space-y-2">
                    {rotationButton(entry.id, pages.pageNumbers[index]!)}
                    <img
                    src={src}
                    alt={t('invoiceScan.page', { index: row?.pdfPages ? pages.pageNumbers[index] : pageNumber })}
                    className="mx-auto w-full max-w-2xl rounded-lg border border-border bg-white"
                    />
                  </div>
                );
              })}
              {pages.total > pages.pages.length && (
                <p className="text-center text-xs text-muted-foreground">
                  {t('invoiceScan.pagesCut', { shown: pages.pages.length, total: pages.total })}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
